import { useMemo } from "react";
import {
  Box,
  Flex,
  HStack,
  Stack,
  Text,
  chakra,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  FlexProps,
} from "@chakra-ui/react";

type Direction = "horizontal" | "vertical" | "wrap";
type Primitive = string | number | boolean;

export type RadioOption<T extends Primitive = Primitive> = {
  value: T;
  label: string;
  isDisabled?: boolean;
};

type RootProps = Omit<FlexProps, "children" | "direction" | "onChange" | "value">;
export type RadioGroupProps<T extends Primitive = Primitive> = RootProps & {
  value: T;
  onChange: (next: T) => void;
  options: RadioOption<T>[];
  direction?: Direction;
  elementWidth?: FlexProps["w"];
  isDisabled?: boolean;
  spacing?: number | string;
  size?: "sm" | "md";
  defaultValue?: string;
  isRequired?: boolean;
};

const SIZE = {
  sm: { dot: 18, inner: 10 },
  md: { dot: 20, inner: 12 },
} as const;

const customStyles = {
  base: {
    w: "full",
    borderRadius: "4px",
    boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)",
    px: "14px",
    minH: "48px",
  },
  active: {
    border: "1px solid",
    borderColor: "accent",
    bg: "#DDDBF1",
    color: "#1A202C",
  },
  default: {
    border: "1px solid #E2E8F0",
    bg: "white",
    color: "#1A202C",
  },
};

const generateKey = (value: Primitive) => `${typeof value}:${String(value)}`;

function RadioDot({
  checked,
  disabled,
  size,
  innerSize,
}: {
  checked: boolean;
  disabled: boolean;
  size: number;
  innerSize: number;
}) {
  return (
    <Box
      aria-hidden="true"
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      border="2px solid"
      bg="white"
      position="relative"
      flexShrink={0}
      opacity={disabled ? 0.55 : 1}
      transition="border-color 0.15s ease"
      borderColor={checked ? "equidefi.blue" : "gray.300"}
      _groupHover={disabled || checked ? undefined : { borderColor: "gray.400" }}
      _groupFocusVisible={{
        borderColor: checked ? "equidefi.blue" : "gray.400",
      }}
      _after={
        checked
          ? {
              content: '""',
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: `${innerSize}px`,
              height: `${innerSize}px`,
              borderRadius: "full",
              bg: "equidefi.blue",
            }
          : undefined
      }
    />
  );
}

function RadioItem({
  label,
  isDisabled,
  size,
  radioProps,
}: {
  label: string;
  isDisabled: boolean;
  size: "sm" | "md";
  radioProps: UseRadioProps;
}) {
  const { state, getInputProps, getRadioProps, getLabelProps } = useRadio(radioProps);

  const checked = state.isChecked;
  const disabled = isDisabled;

  const { dot, inner } = SIZE[size];
  const stateStyles = checked ? customStyles.active : customStyles.default;
  const hoverBg = checked ? "#d4d1eb" : "#f7fafc";

  return (
    <chakra.label display="block" w="full" cursor={disabled ? "not-allowed" : "pointer"} opacity={disabled ? 0.6 : 1}>
      <input {...getInputProps()} disabled={disabled} />

      <Box
        {...getRadioProps()}
        role="group"
        display="flex"
        alignItems="center"
        gap={3}
        transition="all 0.15s ease"
        _focusVisible={{ boxShadow: "0 0 0 3px rgba(128, 90, 213, 0.25)" }}
        _hover={disabled ? undefined : { bg: hoverBg }}
        {...customStyles.base}
        {...stateStyles}>
        <RadioDot checked={checked} disabled={disabled} size={dot} innerSize={inner} />

        <Text {...getLabelProps()} mb={0} flex="1">
          {label}
        </Text>
      </Box>
    </chakra.label>
  );
}

export function RadioGroup<T extends Primitive>({
  value,
  onChange,
  options,
  direction = "horizontal",
  elementWidth = "50%",
  isDisabled = false,
  spacing = 4,
  mb = 5,
  size = "md",
  defaultValue,
  ...rest
}: RadioGroupProps<T>) {
  const selectedKey = useMemo(() => {
    const idx = options.findIndex((o) => Object.is(o.value, value));
    return idx >= 0 ? String(idx) : undefined;
  }, [options, value]);

  const { getRootProps, getRadioProps } = useRadioGroup({
    value: selectedKey,
    defaultValue,
    onChange: (key) => {
      const idx = Number(key);
      const next = options[idx]?.value;
      if (next !== undefined) onChange(next);
    },
  });

  const groupProps = getRootProps();

  if (direction === "wrap") {
    return (
      <Flex {...groupProps} w="full" mb={mb} gap={spacing} flexWrap="wrap" {...rest}>
        {options.map((option, i) => {
          const key = generateKey(option.value);
          const disabled = isDisabled || !!option.isDisabled;
          const radioProps = getRadioProps({ value: String(i) });

          return (
            <Box key={key} flex={`1 1 ${elementWidth}`} minW={elementWidth}>
              <RadioItem label={option.label} isDisabled={disabled} size={size} radioProps={radioProps} />
            </Box>
          );
        })}
      </Flex>
    );
  }

  const Container = direction === "horizontal" ? HStack : Stack;

  return (
    <Container {...groupProps} spacing={spacing} mb={mb} w="full" {...rest}>
      {options.map((option, i) => {
        const key = generateKey(option.value);
        const disabled = isDisabled || !!option.isDisabled;
        const radioProps = getRadioProps({ value: String(i) });

        return (
          <Box key={key} w="full" flex={direction === "horizontal" ? 1 : undefined}>
            <RadioItem label={option.label} isDisabled={disabled} size={size} radioProps={radioProps} />
          </Box>
        );
      })}
    </Container>
  );
}
